/**
 * Template `@font-face` consumption (Ingest Fidelity IF-4).
 *
 * A crawl vendors the site's self-hosted font binaries into its reference
 * bundle, and then every consumer threw them away: the preview deleted every
 * `@font-face` rule outright, so a template whose whole identity is Montserrat
 * rendered in the builder's default stack.
 *
 * Under font policy v1 the faces are REWRITTEN instead of deleted: each `src`
 * url() that resolves to a vendored binary becomes a `pb-vendored://` URI that
 * the preview, both exporters and the saved-preview bundler each resolve for
 * themselves. Every authored descriptor survives — variable weight ranges,
 * `unicode-range`, `font-display`, `format()`, `font-style` — because a static
 * substitute for a variable font is a different typeface, not a fallback.
 *
 * Two rules make the result honest rather than merely present:
 *
 * - **All-or-block per family.** If any face of a family has no vendored
 *   binary, EVERY face of that family is dropped and reported. Emitting the
 *   subset that happens to be vendored would silently change which weights and
 *   which unicode ranges the browser can render, and the missing ones would
 *   fall back mid-paragraph.
 * - **No live-origin URLs survive.** A src component that cannot be mapped is
 *   removed rather than left pointing at the captured site, so a template can
 *   never quietly depend on an origin that may be gone.
 *
 * `@import` is always removed, under either policy: it is a render-blocking
 * request to a third party and nothing vendors it.
 */

import * as csstree from '../vendor/csstree.esm.js';
import { buildVendoredUri } from './vendored-asset-uri.js';

export const FONT_POLICY_NONE = 0;
export const FONT_POLICY_VENDORED = 1;

const FONT_AT_RULES = new Set(['font-face', 'import']);

/**
 * Absolute form of a CSS url() as the crawler would have recorded it, so the
 * value can be looked up in the reference bundle's `vendoredAssets` map (whose
 * keys are absolute request URLs).
 *
 * The vendoring helper resolved each url() against ITS OWN stylesheet's URL.
 * The parser merges every stylesheet into one file, so that base is gone; a
 * genuinely relative path (`../fonts/x.woff2`) is therefore UNRESOLVABLE here
 * and reported as such rather than guessed at against the site root, which
 * would produce a confident wrong key. Absolute, protocol-relative and
 * root-relative urls — every form the corpus actually contains — resolve
 * exactly.
 *
 * @returns {string} the absolute URL, or '' when it cannot be resolved.
 */
export function absolutizeFontUrl(value, domain) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;
  const host = String(domain || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  if (!host) return '';
  if (raw.startsWith('/')) return `https://${host}${raw}`;
  return '';
}

function stripUrlFragment(value) {
  return String(value || '')
    .split('#')[0]
    .replace(/\?$/, '');
}

function relativeManifestKey(value) {
  const raw = stripUrlFragment(value).trim();
  if (!raw) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//') || raw.startsWith('/')) return '';
  return raw.replace(/^(?:\.\.\/|\.\/)+/g, '').replace(/^\/+/, '');
}

function manifestPathSearchKey(value) {
  try {
    const parsed = new URL(stripUrlFragment(value));
    return `${parsed.pathname.replace(/^\/+/, '')}${parsed.search}`;
  } catch {
    return '';
  }
}

function lookupVendoredFontRel(original, domain, map) {
  const absolute = absolutizeFontUrl(original, domain);
  if (absolute) return map[absolute] || map[stripUrlFragment(absolute)] || '';

  const suffix = relativeManifestKey(original);
  if (!suffix) return '';

  const matches = new Set();
  for (const [source, rel] of Object.entries(map)) {
    const key = manifestPathSearchKey(source);
    if (key === suffix || key.endsWith(`/${suffix}`)) matches.add(rel);
  }
  return matches.size === 1 ? [...matches][0] : '';
}

function declarationValueString(decl) {
  try {
    return csstree.generate(decl.value).trim();
  } catch {
    return '';
  }
}

/** First font-family name declared in an @font-face block, unquoted. */
function faceFamilyName(atrule) {
  const children = atrule.block?.children;
  if (!children) return '';
  let family = '';
  children.forEach((decl) => {
    if (family || decl?.type !== 'Declaration') return;
    if (String(decl.property || '').toLowerCase() !== 'font-family') return;
    family = declarationValueString(decl).replace(/^["']|["']$/g, '');
  });
  return family;
}

/**
 * Split a `src` value into its comma-separated components, each carrying the
 * Url node it contains (if any). Order is preserved so a rewrite keeps the
 * authored format() preference list intact.
 */
function srcComponents(decl) {
  const children = decl.value?.children;
  if (!children) return [];
  const groups = [];
  let current = { nodes: [], url: null };
  children.forEach((node) => {
    if (node.type === 'Operator' && String(node.value).trim() === ',') {
      groups.push(current);
      current = { nodes: [], url: null };
      return;
    }
    current.nodes.push(node);
    if (node.type === 'Url' && !current.url) current.url = node;
  });
  groups.push(current);
  return groups.filter((group) => group.nodes.length > 0);
}

/**
 * Rebuild a `src` value from the components that survived, re-inserting exactly
 * one comma between them. Rebuilding beats surgical removal: the separator a
 * dropped component leaves behind belongs to neither neighbour, and every
 * attempt to decide that in place gets one of the edge cases (first component,
 * last component, two adjacent drops) wrong.
 */
function keepSrcComponents(decl, components, doomed) {
  if (doomed.size === 0) return;
  const rebuilt = new csstree.List();
  let first = true;
  components.forEach((component, index) => {
    if (doomed.has(index)) return;
    if (!first) rebuilt.appendData({ type: 'Operator', value: ',' });
    first = false;
    for (const node of component.nodes) rebuilt.appendData(node);
  });
  decl.value.children = rebuilt;
}

/**
 * Rewrite a template stylesheet's font at-rules for the vendored-font policy.
 *
 * @param {string} css
 * @param {object} options
 * @param {string} options.domain - template site name (vendored bundle owner)
 * @param {Record<string,string>} options.vendoredAssets - absolute source URL →
 *   bundle-relative vendored path, from the reference bundle's `_manifest.json`
 * @returns {{css: string, kept: Array<{family: string, uris: string[]}>,
 *            dropped: Array<{family: string, reason: string, urls: string[]}>,
 *            importsRemoved: number}}
 */
export function rewriteTemplateFontRules(css, { domain, vendoredAssets } = {}) {
  const raw = String(css || '');
  const empty = { css: raw, kept: [], dropped: [], importsRemoved: 0 };
  if (!raw || (!/@font-face/i.test(raw) && !/@import/i.test(raw))) return empty;

  let ast;
  try {
    ast = csstree.parse(raw);
  } catch (err) {
    console.warn('[template-font-rules] parse failed:', err.message);
    return empty;
  }

  const map = vendoredAssets && typeof vendoredAssets === 'object' ? vendoredAssets : {};
  /** @type {Array<{family: string, item: object, list: object, uris: string[], unresolved: string[]}>} */
  const faces = [];
  let importsRemoved = 0;

  csstree.walk(ast, {
    visit: 'Atrule',
    enter(node, item, list) {
      const name = String(node.name || '').toLowerCase();
      if (!FONT_AT_RULES.has(name) || !item || !list) return;
      if (name === 'import') {
        list.remove(item);
        importsRemoved += 1;
        return;
      }

      const family = faceFamilyName(node);
      const record = {
        family,
        item,
        list,
        uris: [],
        // Sources removed from a face that still has a usable one — a legacy
        // .woff beside a vendored .woff2, say. Reported, never fatal: the
        // browser would have taken the woff2 anyway.
        droppedSources: [],
        // True when the face ended up with at least one usable source.
        usable: false,
        srcSeen: false
      };
      node.block?.children?.forEach((decl) => {
        if (decl?.type !== 'Declaration') return;
        if (String(decl.property || '').toLowerCase() !== 'src') return;
        record.srcSeen = true;
        const components = srcComponents(decl);
        const doomed = new Set();
        let survivors = 0;
        components.forEach((component, index) => {
          const urlNode = component.url;
          if (!urlNode) {
            // `local("Montserrat")` and friends: a real source that needs no
            // binary. Kept as authored.
            survivors += 1;
            return;
          }
          const original = String(urlNode.value || '');
          if (original.startsWith('data:')) {
            survivors += 1; // self-contained already
            return;
          }
          const rel = lookupVendoredFontRel(original, domain, map);
          const uri = rel ? buildVendoredUri(domain, rel) : '';
          if (!uri) {
            doomed.add(index);
            record.droppedSources.push(original || '(no url)');
            return;
          }
          urlNode.value = uri;
          record.uris.push(uri);
          survivors += 1;
        });
        if (survivors > 0) {
          record.usable = true;
          keepSrcComponents(decl, components, doomed);
        }
      });
      faces.push(record);
    }
  });

  // All-or-block: if ANY face of a family lost its every source, the whole
  // family goes. Shipping the subset that happened to vendor would silently
  // change which weights and which characters render in the site typeface —
  // and the missing ones fall back mid-paragraph, which is worse than a
  // consistent fallback everywhere.
  const byFamily = new Map();
  for (const face of faces) {
    const key = face.family.toLowerCase();
    if (!byFamily.has(key)) byFamily.set(key, []);
    byFamily.get(key).push(face);
  }

  const kept = [];
  const dropped = [];
  for (const [, group] of byFamily) {
    const family = group[0].family;
    const broken = group.filter((face) => !face.srcSeen || !face.usable);
    if (broken.length > 0) {
      const urls = Array.from(new Set(broken.flatMap((face) => face.droppedSources)));
      for (const face of group) face.list.remove(face.item);
      dropped.push({
        family,
        reason: 'no vendored binary for every face of this family',
        urls
      });
      continue;
    }
    kept.push({
      family,
      uris: Array.from(new Set(group.flatMap((face) => face.uris))),
      droppedSources: Array.from(new Set(group.flatMap((face) => face.droppedSources)))
    });
  }

  let out;
  try {
    out = csstree.generate(ast);
  } catch (err) {
    console.warn('[template-font-rules] generate failed:', err.message);
    return empty;
  }

  kept.sort((a, b) => a.family.localeCompare(b.family));
  dropped.sort((a, b) => a.family.localeCompare(b.family));
  return { css: out, kept, dropped, importsRemoved };
}

function componentText(component) {
  return component.nodes
    .map((node) => {
      try {
        return csstree.generate(node);
      } catch {
        return '';
      }
    })
    .join(' ')
    .toLowerCase();
}

function isLegacyFontSource(value, component) {
  const raw = String(value || '').toLowerCase();
  if (/\.(?:eot|svg)(?:[?#]|$)/i.test(raw)) return true;
  return /format\(\s*["']?(?:embedded-opentype|svg)["']?\s*\)/i.test(componentText(component));
}

function preferredVendoredSource(components) {
  let first = '';
  for (const component of components) {
    const value = String(component.url?.value || '');
    if (!value.startsWith('pb-vendored://')) continue;
    if (!first) first = value;
    if (!isLegacyFontSource(value, component)) return value;
  }
  return first;
}

/**
 * The vendored faces worth a `<link rel="preload">`.
 *
 * Preload forces a download, so preloading every face of a subsetted family
 * defeats the subsetting: a latin-only page would fetch the latin-ext binary it
 * will never render. A face qualifies when it declares no `unicode-range` at
 * all, or when its range covers the basic-latin block the page's own text sits
 * in. Everything else still ships and still loads — on demand, which is what
 * the subsetting was for.
 *
 * @param {string} css
 * @returns {string[]} `pb-vendored://` URIs, in document order
 */
export function collectVendoredFontPreloadUris(css) {
  return collectVendoredFontPreloadFaces(css).map((face) => face.uri);
}

/**
 * The same faces, carrying the family each one declares.
 *
 * A consumer that has to decide whether a face was *supposed* to be fetched
 * needs the family: a face is only downloaded when a glyph in its range is
 * painted, so an icon family whose glyphs this page never renders is absent
 * from the network log by design, not by defect.
 *
 * @param {string} css
 * @returns {Array<{family: string, uri: string}>} in document order
 */
export function collectVendoredFontPreloadFaces(css) {
  const raw = String(css || '');
  if (!raw.includes('@font-face') || !raw.includes('pb-vendored://')) return [];
  let ast;
  try {
    ast = csstree.parse(raw);
  } catch {
    return [];
  }
  const faces = [];
  const seen = new Set();
  csstree.walk(ast, {
    visit: 'Atrule',
    enter(node) {
      if (String(node.name || '').toLowerCase() !== 'font-face') return;
      let range = null;
      let srcDecl = null;
      let family = '';
      // Weight and style ride along so a consumer can ask whether the page
      // paints THIS face, not merely this family. A family declared at 400 and
      // 900 whose only use is `font-weight:900` never fetches the 400 file —
      // demanding it reports a defect where none exists.
      let weight = '400';
      let style = 'normal';
      node.block?.children?.forEach((decl) => {
        if (decl?.type !== 'Declaration') return;
        const prop = String(decl.property || '').toLowerCase();
        if (prop === 'unicode-range') range = declarationValueString(decl).toUpperCase();
        if (prop === 'src') srcDecl = decl;
        if (prop === 'font-weight') weight = declarationValueString(decl).trim() || '400';
        if (prop === 'font-style') style = declarationValueString(decl).trim() || 'normal';
        if (prop === 'font-family') {
          family = declarationValueString(decl)
            .trim()
            .replace(/^["']|["']$/g, '');
        }
      });
      // U+0000-00FF (or any range whose first block starts at U+0000/U+0020) is
      // the ASCII face; a range that starts higher is a supplementary subset.
      if (
        range !== null &&
        !/^U\+0{1,4}(-|$|,)/.test(range) &&
        !/^U\+0{0,2}20(-|$|,)/.test(range)
      ) {
        return;
      }
      const value = srcDecl ? preferredVendoredSource(srcComponents(srcDecl)) : '';
      if (!value || seen.has(value)) return;
      seen.add(value);
      faces.push({ family, uri: value, weight, style });
    }
  });
  return faces;
}

/**
 * Families a stylesheet declares with vendored `@font-face` rules. The export
 * font fetcher subtracts these from what it asks GWFH for: substituting static
 * instances for a family whose real (often variable) binaries already ship
 * would declare two competing face sets for one family name.
 */
/**
 * Families a stylesheet declares with a SELF-CONTAINED `@font-face` — one whose
 * source is a `data:` URI, so the glyphs are already in the CSS and no network
 * request can help or hurt.
 *
 * Carousel and icon libraries ship exactly this shape. Swiper embeds
 * `@font-face{font-family:"swiper-icons";src:url(data:application/font-woff;base64,…)}`
 * for its arrow glyphs, so `swiper-icons` looked to the export font fetcher like
 * a webfont it should go and find — and it asked Google Web Fonts Helper for a
 * family that has never existed there, once per export, cross-origin.
 *
 * Deliberately narrower than "has any @font-face": a face pointing at a remote
 * URL might be dead, and skipping the fetch for one of those would leave a
 * template with no working font at all. A data: URI cannot fail to load, so
 * subtracting it is always safe.
 */
export function collectSelfContainedFontFaceFamilies(css) {
  const raw = String(css || '');
  if (!raw.includes('@font-face') || !raw.includes('data:')) return [];
  let ast;
  try {
    ast = csstree.parse(raw);
  } catch {
    return [];
  }
  const families = new Set();
  csstree.walk(ast, {
    visit: 'Atrule',
    enter(node) {
      if (String(node.name || '').toLowerCase() !== 'font-face') return;
      let selfContained = false;
      csstree.walk(node, {
        visit: 'Url',
        enter(urlNode) {
          if (/^data:/i.test(String(urlNode.value || '').trim())) selfContained = true;
        }
      });
      if (!selfContained) return;
      const family = faceFamilyName(node);
      if (family) families.add(family);
    }
  });
  return Array.from(families);
}

export function collectVendoredFontFaceFamilies(css) {
  const raw = String(css || '');
  if (!raw.includes('@font-face') || !raw.includes('pb-vendored://')) return [];
  let ast;
  try {
    ast = csstree.parse(raw);
  } catch {
    return [];
  }
  const families = new Set();
  csstree.walk(ast, {
    visit: 'Atrule',
    enter(node) {
      if (String(node.name || '').toLowerCase() !== 'font-face') return;
      let vendored = false;
      csstree.walk(node, {
        visit: 'Url',
        enter(urlNode) {
          if (String(urlNode.value || '').startsWith('pb-vendored://')) vendored = true;
        }
      });
      if (!vendored) return;
      const family = faceFamilyName(node);
      if (family) families.add(family);
    }
  });
  return Array.from(families);
}
